/* eslint-disable camelcase -- snake_case fields come straight from the mandate API shape */
import { useCallback } from "react";
import {
  useAuthMandate,
  useAuthorizeMandate,
  useDeleteMandate,
  useGeneratePrefilledMandate,
  useMandates,
  useMandateStatusReport,
  usePollMandateStatus,
  webDownload,
} from "creso-core/hooks";
import type { Mandate } from "creso-core/types";
import { toast } from "sonner";
import { handleError } from "../../utils/error";

export function useMandateActions(investmentAccountId: string) {
  const { mandates, isLoading, isFetching } = useMandates(investmentAccountId);

  const {
    mutateAsync: refreshMandateStatuses,
    isPending: isRefreshingStatuses,
  } = useMandateStatusReport(investmentAccountId);

  const { mutateAsync: authorizeMandate, isPending: isNotifying } =
    useAuthorizeMandate();
  const { mutateAsync: pollMandateStatus, isPending: isPolling } =
    usePollMandateStatus();
  const { mutateAsync: authMandate, isPending: isAuthorizing } =
    useAuthMandate();
  const { mutateAsync: deleteMandate } = useDeleteMandate(investmentAccountId);
  const { mutateAsync: generatePrefilledMandate } =
    useGeneratePrefilledMandate();

  const refresh = useCallback(async () => {
    try {
      await refreshMandateStatuses();
      toast.success("Mandates refreshed");
    } catch (error) {
      handleError(error, "Failed to refresh mandates");
    }
  }, [refreshMandateStatuses]);

  const notify = useCallback(
    async (mandate: Mandate) => {
      try {
        await authorizeMandate({
          id: mandate.id,
          investment_account_guid: investmentAccountId,
          sendEmail: true,
        });
        toast.success("Notification sent to client");
      } catch (error) {
        handleError(error, "Failed to notify client");
      }
    },
    [authorizeMandate, investmentAccountId]
  );

  const selfAuthorize = useCallback(
    async (mandate: Mandate) => {
      try {
        await pollMandateStatus({
          id: mandate.id,
          investment_account_guid: investmentAccountId,
        });
        const response = await authMandate({
          id: mandate.id,
          investment_account_guid: investmentAccountId,
        });
        if (response.firstHolderLink) {
          window.location.href = response.firstHolderLink;
        } else {
          toast.error("Authorisation link is not available yet");
        }
      } catch (error) {
        handleError(error, "Failed to authorise mandate");
      }
    },
    [authMandate, pollMandateStatus, investmentAccountId]
  );

  const download = useCallback(
    async (mandate: Mandate) => {
      try {
        const { pdf_url } = await generatePrefilledMandate({
          mandateId: mandate.id,
          investmentAccountId,
        });
        const response = await fetch(pdf_url);
        if (!response.ok) {
          throw new Error();
        }
        const blob = await response.blob();
        await webDownload(blob, `nach-mandate-${mandate.id}.pdf`);
        toast.success("Physical mandate form downloaded");
      } catch (error) {
        handleError(error, "Failed to download physical mandate form");
      }
    },
    [generatePrefilledMandate, investmentAccountId]
  );

  const remove = useCallback(
    async (mandate: Mandate) => {
      try {
        await deleteMandate(mandate.id);
      } catch (error) {
        handleError(error, "Failed to delete mandate");
      }
    },
    [deleteMandate]
  );

  return {
    mandates,
    isLoading,
    isFetching: isFetching || isRefreshingStatuses,
    isNotifying,
    isAuthorizing: isPolling || isAuthorizing,
    refresh,
    notify,
    selfAuthorize,
    download,
    remove,
  };
}
